// THE APP STRIP — the Dock (DESIGN-WL-HOME-TOP-STRIP-SUMMARY-1; Nika's pick
// 2026-09-28 row 1 "A or C": both ship as a personal option, this file is A).
// ONE quiet glass capsule of calm glyph tiles between the greeting and the
// board: one line, the names on hover and focus (or under the glyphs when the
// person turns names on), scrolling sideways with an edge fade when the apps
// outgrow it, closing with "All apps" (the search launcher). App icons are
// never grid items (HOME_WIDGETS_SPEC §1, decision H1 A).
//
// THE PERSON'S LOOK (functions/homeLook.js, the Personalise panel): the strip
// hides when it is off; in the Rail style the Rail (components/HomeAppRail.js)
// stands beside the board and this row steps aside — except on a phone, where
// the Rail steps aside and this row stays (a static rule keyed on
// `data-strip-style`, never a width read in code).
//
// The page supplies the items (pages/main.js `_appStripRows`): the app
// shortcuts in the saved layout order, the user's hides kept, each a state row
// `{ id, title, icon, openRoute, … }` — the frame's descriptor shape, so an
// item opens through the SAME path a card does (`homeWidgetOpen`). With no
// app left to show the page hides the whole strip (`_appStripHidden`) — never
// a capsule holding "All apps" alone.
//
// CALM GLYPHS: a neutral `layer` tile with the app's glyph in the title ink —
// no per-app hue on the strip (the launcher keeps its colours). The capsule is
// the skin's `pill` (glass on the ambient ground, the header glass on solid);
// the component never reads the skin.
//
// NAMES NEVER CUT (quality law, HOME_AMBIENT_SKIN_SPEC §3): a shown name breaks
// only between words, never inside one; an item is as wide as its name on one
// line up to a cap, and never narrower than its longest word (min-content
// beats the cap). A hidden name is the button's accessible name and its
// tooltip — never a clipped label.
//
// Motion: the strip enters as ONE unit (fadeInUpShort, C) — never item by
// item; the brand reset lands it at once under reduced motion.
//
// Edit mode (`root._homeCustomizeMode`): an item opens nothing; its remove
// button hides it (the same hide pref as a card); the arrow keys move it in
// the order (homeWidgetKeyMove).
export const HomeAppStrip = {
  tag: 'nav',
  class: 'home-app-strip',
  'aria-label': (el) => el.call('polyglot', 'workspace.home.appStrip'),
  // The row is the board's width (the board stylesheet's `.home-app-strip`);
  // it scrolls the capsule sideways and fades only an edge that hides apps
  // (the kit's one edge-fade row).
  extends: 'AppScrollRow',
  display: 'flex',
  alignItems: 'flex-start',
  flexShrink: '0',
  // Room for the focus ring above and below the items (the row scrolls, so
  // its box clips): the ring is never cut.
  padding: 'X',
  hide: (el, s) => el.call('homeAppStripLookHidden', s.root, 'dock'),
  attr: {
    'data-strip-style': (el, s) => (el.call('homeLookRead', s.root) || {}).stripStyle,
    'data-strip-labels': (el, s) =>
      (el.call('homeLookRead', s.root) || {}).stripLabels ? 'true' : null
  },
  // The Rail stands beside the board instead — above the phone step only.
  // ONE rule inside the media block (the shell's `aboveMobileL`, the exact
  // complement of the Rail's own `mobileL` step-aside): a base rule plus a
  // phone override would leave the winner to the atomic engine's sheet order,
  // and a media query nested INSIDE a selector block compiles to nothing.
  '@aboveMobileL': { '&[data-strip-style="rail"]': { display: 'none' } },

  animation: 'fadeInUpShort',
  animationDuration: 'C',
  animationTimingFunction: 'defaultBezier',
  animationFillMode: 'backwards',

  DockCapsule: {
    class: 'home-app-dock',
    theme: 'pill',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'Y',
    flexShrink: '0',
    width: 'fit-content',
    padding: 'Y',
    round: 'radiusPill',
    // Names under the glyphs make the capsule taller: a card radius then (yesterday's look, Nika 2026-10-02:
    // "yesterdays version of DockCapsule was better" — __tests__/dockYesterdayLook.test.js pins it). The one
    // later fix kept: with names ON the gap steps to Z1, because at Y two Georgian names touched (a defect, not a
    // look); the default (names off) is exactly yesterday's.
    ':is(.home-app-strip[data-strip-labels]) &': { round: 'radiusCard', padding: 'Y Z', gap: 'Z1' },

    Items: {
      tag: 'fragment',
      childExtends: 'HomeAppStripItem',
      childrenAs: 'state',
      children: []
    },

    // The last item: the whole catalogue (the header's search launcher).
    HomeAppStripAll: {}
  }
}

// "All apps" — the last item of the Dock and of the Rail: the whole catalogue
// (the header's search launcher), never removable. Pressed again, it closes
// the launcher it opened (functions/searchApps.js toggleSearchAppsDropdown).
export const HomeAppStripAll = {
  extends: 'HomeAppStripItem',
  // its own class beside the item's (a declared class REPLACES the inherited one): the Rail's index skips it
  class: 'home-app-strip-item home-app-strip-all',
  state: { all: true },
  Open: {
    'aria-label': (el) => el.call('polyglot', 'workspace.home.allApps'),
    tooltipText: (el, s) =>
      (el.call('homeLookRead', s.root) || {}).stripLabels
        ? ''
        : el.call('polyglot', 'workspace.home.allApps'),
    onClick: (e, el) => {
      e.stopPropagation()
      el.call('toggleSearchAppsDropdown', { anchorEl: el, restoreEl: el })
    },
    Glyph: { Icon: { name: 'grid' } },
    RailGlyph: { Icon: { name: 'lnGrid' } },
    Label: { text: '{{ workspace.home.allApps | polyglot }}' }
  },
  CloseButton: { hide: true }
}

// One item: the OPEN button (the glyph and, when names are on, the name — one
// control, one accessible name "Open {app}") and, in edit mode, the remove
// button beside it (a sibling, never a button inside a button). The Rail
// reuses it as a row (the `:is(.home-app-rail) &` rules below).
export const HomeAppStripItem = {
  class: 'home-app-strip-item',
  attr: { 'data-widget-id': (el, s) => (s && s.id) || null },
  position: 'relative',
  flex: '0 0 auto',
  scrollSnapAlign: 'start',
  display: 'flex',
  width: 'fit-content',
  minWidth: 'min-content',
  maxWidth: 'thumbnailLg',
  // In the Rail (components/HomeAppRail.js) an item is a row: the glyph, and
  // the name beside it when names are on.
  // The Rail widens by its index column (the item cap F plus B), so a Georgian name never meets its number.
  ':is(.home-app-rail) &': { maxWidth: 'F+B', width: '100%' },
  // THE RAIL INDEX (dropdowns spec §12.1 N1): the first nine apps carry 01…09 — the 1-9 shortcut, read in the
  // same capsule order (closer 6449a5ab7). "All apps" is not numbered.
  ':is(.home-app-rail) &:not(.home-app-strip-all)': { counterIncrement: 'rail-app' },

  Open: {
    // The name on hover and focus while names are off (the shell's one
    // tooltip root, @symbo.ls/tooltip).
    // the one state logic of the glyph plate (shared/components/AppGlyphPlate.js GlyphPlateHolder)
    extends: ['TooltipTrigger', 'GlyphPlateHolder'],
    tag: 'button',
    type: 'button',
    class: 'home-app-strip-open',
    'aria-label': (el, s) =>
      s && s.id
        ? el
            .call('polyglot', 'workspace.menu.openApp')
            .replace('{app}', el.call('polyglot', s.title || s.id))
        : null,
    tooltipText: (el, s) =>
      s && s.id && !(el.call('homeLookRead', s.root) || {}).stripLabels
        ? el.call('polyglot', s.title || s.id)
        : '',
    // Inside the Rail (an ANCESTOR whose class is the rail's — a key lookup
    // would also find the page's Rail beside the Dock) the name shows to the
    // right; in the Dock, below.
    tooltipPlacement: (el) => (el.lookup((p) => p.class === 'home-app-rail') ? 'right' : 'bottom'),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    ':is(.home-app-rail) &': { flexDirection: 'row', justifyContent: 'flex-start', gap: 'Z' },
    gap: 'X',
    width: '100%',
    minWidth: 'min-content',
    padding: '0',
    border: 'none',
    background: 'transparent',
    color: 'inherit',
    cursor: 'pointer',
    round: 'radiusControl',
    // THE DOCK (yesterday's look, Nika 2026-10-02): the tile's wash steps up under the pointer and on press; the
    // house focus ring (the reset) draws on the button. THE RAIL's plate (its own class) takes the one state logic
    // of the glyph plate from GlyphPlateHolder above. The app you are in says so to assistive tech in both.
    ':hover .home-app-strip-tile': { background: 'selected' },
    ':active .home-app-strip-tile': { background: 'selected', transform: 'scale(0.96)' },
    'aria-current': (el, s) =>
      s && s.openRoute && el.call('appRouteIsCurrent', s.openRoute, s.root) ? 'page' : null,
    // The Rail's index: a mono number at the row's end (aria-hidden by being generated content with an empty
    // alternative text — `/ ""`), only for the first nine apps.
    ':is(.home-app-rail) &::after': {
      content: 'counter(rail-app, decimal-leading-zero) / ""',
      fontFamily: 'Code',
      fontSize: 'Y',
      color: 'inactive',
      marginLeft: 'auto',
      paddingLeft: 'Z'
    },
    ':is(.home-app-rail) .home-app-strip-item:nth-child(n+10) &::after': { content: 'none' },
    ':is(.home-app-rail) .home-app-strip-all &::after': { content: 'none' },
    onClick: (e, el, s) => {
      e.stopPropagation()
      if (s && s.root && s.root._homeCustomizeMode) return
      el.call('homeWidgetOpen', s, e)
    },
    // Warm the app's chunk while the pointer rests on the tile or focus
    // reaches it — the click then opens without the network round trip
    // (functions/slotPrefetch.js).
    onPointerenter: (e, el, s) => el.call('prefetchSlotRoute', el.call('homeWidgetOpenRoute', s)),
    onFocus: (e, el, s) => el.call('prefetchSlotRoute', el.call('homeWidgetOpenRoute', s)),
    // Edit mode: the arrow keys move an app in the order.
    onKeydown: (e, el, s) => {
      if (s && s.id) el.call('homeWidgetKeyMove', e, s)
    },

    // THE DOCK's glyph — yesterday's calm tile (components/AppGlyphTile.js: the `layer` wash, `title` ink, the
    // app's own icon), the strip's hover / press hook class (the item's rules above step its wash). Nika
    // 2026-10-02: "yesterdays version of DockCapsule was better". Not in the Rail (the RailGlyph below).
    Glyph: {
      extends: 'AppGlyphTile',
      class: 'home-app-strip-tile',
      if: (el) => !el.lookup((p) => p.class === 'home-app-rail')
    },
    // THE RAIL's glyph (dropdowns spec §12.3 N1): the shared plate at the strip's 44 px, the app's LINE glyph at
    // 24 by the item's open route through the one resolver; inverted by GlyphPlateHolder; its own class, so the
    // Dock's hover hook never reaches it.
    RailGlyph: {
      extends: 'AppGlyphPlate',
      class: 'home-app-rail-tile',
      if: (el) => !!el.lookup((p) => p.class === 'home-app-rail'),
      boxSize: 'controlH',
      minWidth: 'controlH',
      Icon: {
        name: (el, s) =>
          el.call(
            'glyphNameFor',
            { route: s && s.openRoute, icon: s && s.icon, glyph: s && s.glyph },
            'ln'
          ),
        boxSize: 'iconLg'
      }
    },

    Label: {
      tag: 'span',
      class: 'home-app-strip-label',
      hide: (el, s) => !(el.call('homeLookRead', s.root) || {}).stripLabels,
      maxWidth: '100%',
      fontSize: 'Z',
      fontWeight: '500',
      // the house leading: TBCContractica's Georgian descenders need 1.5
      lineHeight: '1.5',
      // ADAPTIVE `title` ink (measured 14.38:1 light, 8.44:1 dark on the board)
      color: 'title',
      textAlign: 'center',
      ':is(.home-app-rail) &': { textAlign: 'start' },
      overflowWrap: 'normal',
      wordBreak: 'normal',
      textWrap: 'balance',
      text: (el, s) => el.call('polyglot', (s && (s.title || s.id)) || '')
    }
  },

  // Edit mode — an overlay, so entering edit mode moves nothing (Rule 66):
  // the same hide as a card's remove button.
  CloseButton: {
    class: 'home-app-strip-remove',
    // The edit flag is read FIRST, on every run, so the factory tracks it
    // whatever the row, and it is declared (FA515); "All apps" (no id) never
    // shows it.
    hideDeps: [(el, s) => s.root._homeCustomizeMode],
    hide: (el, s) => {
      const editing = !!(s && s.root && s.root._homeCustomizeMode)
      return !editing || !(s && s.id)
    },
    position: 'absolute',
    top: '0',
    left: '0',
    zIndex: '2',
    theme: 'layer',
    // Named for ITS app — "Hide “{title}”" (homeWidgetHideLabel), never one
    // shared "Hide this widget" for every shortcut.
    title: (el, s) => el.call('homeWidgetHideLabel', s),
    'aria-label': (el, s) => el.call('homeWidgetHideLabel', s),
    onClick: (e, el, s) => {
      e.stopPropagation()
      const ops = s && s.root && s.root._homeOps
      if (ops && typeof ops.hideWidget === 'function') ops.hideWidget(s.id)
    }
  }
}
